import { useQuery } from '@tanstack/react-query';
import { TRequestHandlerError } from '@studio-toolkit/request-handler';

import { useQuantum } from 'hooks';
import { apiUrl, requestHandler } from 'utils';
import { useTokenAndHeaders } from 'hooks/useHeaders';

export interface IChangeLog {
  version: string;
  releasedAt: string;
  description: string;
  detail: string[];
}

export interface IOrderTypeChangelogData {
  total: number;
  changeLogs: IChangeLog[];
}

export interface IOrderTypeChangelogApiResponse {
  status: number;
  message: string;
  data: IOrderTypeChangelogData;
}

const useFetchOrderTypeChangelog = (collateralId?: string) => {
  const { trackApiCall } = useQuantum();

  const url = apiUrl({
    endpoint: `orderTypes/change-logs`,
    apiEnv: import.meta.env.VITE_APP_ENV,
  });

  const { headers } = useTokenAndHeaders();

  return useQuery<IOrderTypeChangelogData | null, TRequestHandlerError>({
    queryKey: ['fetchOrderTypeChangelog', collateralId],
    queryFn: async () => {
      const response = await requestHandler<IOrderTypeChangelogApiResponse>({
        requestConfig: {
          method: 'GET',
          url,
          headers,
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            console.log(error);
            throw new Error('Failed to fetch changelog');
          },
        },
        quantumHandler: trackApiCall,
      });

      const apiRes = response;
      if (
        !apiRes ||
        apiRes.status !== 200 ||
        !apiRes.data ||
        !Array.isArray(apiRes.data.changeLogs)
      ) {
        throw new Error('Changelog fetch failed');
      }

      return apiRes.data;
    },
    enabled: true,
  });
};

export default useFetchOrderTypeChangelog;
